/**
 * Ngày sinh / ngày dịp không cần năm (QĐ 0480, CEO chốt 5a 28/10/2026).
 *
 * Năm không rõ lưu là BIRTHDAY_NO_YEAR (1904 — năm nhuận, nhận được 29/02).
 * Gương máy chủ: `occasion.NoYear`. Mọi chỗ in ngày sinh đi qua formatBirthday
 * để không bao giờ hiện "12/11/1904", và không tính tuổi khi không có năm.
 */

export const BIRTHDAY_NO_YEAR = 1904;

export type DayMonth = { day: number; month: number; year: number | null };

const ISO = /^(\d{4})-(\d{2})-(\d{2})/;

function isLeap(y: number): boolean {
  return y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
}

function daysInMonth(month: number, year: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Đọc YYYY-MM-DD; năm 1904 ⇒ year = null. Chuỗi hỏng ⇒ null. */
export function parseBirthday(iso: string | null | undefined): DayMonth | null {
  const m = ISO.exec(iso ?? "");
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(month, year)) return null;
  return { day, month, year: year === BIRTHDAY_NO_YEAR ? null : year };
}

/** true khi ngày có năm thật (không phải dấu 1904). */
export function birthdayHasYear(iso: string | null | undefined): boolean {
  return parseBirthday(iso)?.year != null;
}

const two = (n: number) => String(n).padStart(2, "0");

/** "12/11" khi không rõ năm, "12/11/1995" khi có năm, "" khi trống / hỏng. */
export function formatBirthday(iso: string | null | undefined): string {
  const p = parseBirthday(iso);
  if (!p) return "";
  return p.year == null ? `${two(p.day)}/${two(p.month)}` : `${two(p.day)}/${two(p.month)}/${p.year}`;
}

/**
 * Ghép ngày / tháng / năm (năm trống = không rõ) thành YYYY-MM-DD, hoặc trả lý
 * do bằng tiếng Việt khi ngày không có thật.
 */
export function toBirthdayIso(
  day: number,
  month: number,
  year: number | null,
  today = new Date(),
): { iso: string } | { error: string } {
  if (!Number.isInteger(day) || !Number.isInteger(month) || month < 1 || month > 12) {
    return { error: "Tháng phải từ 1 tới 12" };
  }
  const y = year ?? BIRTHDAY_NO_YEAR;
  if (year != null && (year < 1905 || year > today.getFullYear() + 1)) {
    return { error: `Năm ${year} không hợp lệ — bỏ trống ô năm nếu không biết` };
  }
  if (day < 1 || day > daysInMonth(month, y)) {
    return { error: `Tháng ${month} không có ngày ${day}` };
  }
  return { iso: `${y}-${two(month)}-${two(day)}` };
}

/** Kết quả ô nhập Ngày · Tháng · Năm: iso rỗng khi trống hoặc chưa hợp lệ. */
export type DayMonthValue = { iso: string; error: string | null };

/** Ghép ba ô chữ (ngày, tháng, năm tuỳ chọn) — luật của ô DayMonthInput. */
export function evaluateDayMonth(d: string, m: string, y: string): DayMonthValue {
  if (!d.trim() && !m.trim() && !y.trim()) return { iso: "", error: null };
  if (!d.trim() || !m.trim()) return { iso: "", error: "Nhập đủ ngày và tháng" };
  const r = toBirthdayIso(Number(d), Number(m), y.trim() ? Number(y) : null);
  return "iso" in r ? { iso: r.iso, error: null } : { iso: "", error: r.error };
}

/**
 * Số ngày từ `today` tới lần tới của ngày/tháng (0 = hôm nay). 29/02 rơi vào
 * năm không nhuận thì tính 28/02. Ngày hỏng ⇒ null.
 */
export function daysUntilAnnual(iso: string | null | undefined, today = new Date()): number | null {
  const p = parseBirthday(iso);
  if (!p) return null;
  const base = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  for (const y of [today.getFullYear(), today.getFullYear() + 1]) {
    const d = p.month === 2 && p.day === 29 && !isLeap(y) ? 28 : p.day;
    const at = Date.UTC(y, p.month - 1, d);
    if (at >= base) return Math.round((at - base) / 86_400_000);
  }
  return null;
}

/** Nhãn ưu tiên hậu kỳ (8a): dịp còn trong 7 ngày tới. */
export const OCCASION_SOON_DAYS = 7;

/**
 * Tìm "sinh nhật 12/11" trong chữ tự do (ghi chú đơn, sổ ghi chú) cho dòng gợi
 * ý (9a-5). Nhận: "sinh nhật 12/11", "sn 12/11", "SN: 12-11", "sinh nhật ngày
 * 12 tháng 11", "sinh nhật 12/11/1995". Không thấy / ngày không có thật ⇒ null.
 */
export function findBirthdayMention(text: string | null | undefined): { day: number; month: number; year: number | null; match: string } | null {
  const src = (text ?? "").normalize("NFC");
  const patterns = [
    /(?:sinh\s*nhật|\bsn)\s*(?:là|của\s+\S+)?\s*[:-]?\s*(?:ngày\s*)?(\d{1,2})\s*[/.-]\s*(\d{1,2})(?:\s*[/.-]\s*(\d{4}))?/iu,
    /(?:sinh\s*nhật|\bsn)\s*[:-]?\s*(?:ngày\s*)?(\d{1,2})\s*tháng\s*(\d{1,2})(?:\s*năm\s*(\d{4}))?/iu,
  ];
  for (const re of patterns) {
    const m = re.exec(src);
    if (!m) continue;
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = m[3] ? Number(m[3]) : null;
    const r = toBirthdayIso(day, month, year);
    if ("iso" in r) return { day, month, year, match: m[0].trim() };
  }
  return null;
}
